import type { Language } from '@eformulieren/engine'
import type { Text } from '@eformulieren/model'
import type { ReactNode } from 'react'
import { Spinner } from './Spinner.js'

/**
 * De recordnavigator: binnen een reeks naar het vorige of volgende dossier (R-122).
 *
 * **Alleen dit deel van 8.15 valt binnen de scope.** De werkvoorraad, de voortgang over de
 * reeks, de bundelindiening, de reeksdeadline en de uitval staan Uit dit product en horen bij de
 * gastheer (R-119 tot en met R-121, R-123, R-124). Wat blijft is de sprong zelf, en die hoort
 * hier omdat het formulier de invoer heeft die eerst weg moet.
 *
 * **De reeks komt van de gastheer en wordt hier niet uitgerekend.** Hij weet welke dossiers in
 * de werkvoorraad zitten en in welke volgorde; dit product kent alleen de instanties die hij
 * heeft meegegeven. Deze component krijgt daarvan het stuk dat een navigator nodig heeft: waar
 * je bent en wie de buren zijn. Wie de reeks als lijst binnenkrijgt rekent dat zelf uit; wie de
 * positie per sprong opnieuw krijgt, geeft hem gewoon door. Zo verandert deze component niet mee
 * met de weg waarlangs de reeks binnenkomt.
 *
 * **Zonder invoerverlies is de hele eis, en dat is een belofte van de aanroeper.** Deze
 * component roept `onNavigate` aan en wacht; wie hem levert schrijft eerst alle levende
 * sectiedocumenten weg en bevestigt dat, precies zoals een pod die stopt dat doet. Tijdens dat
 * wachten staan beide knoppen uit, want een tweede sprong terwijl de eerste nog wegschrijft is
 * hoe je alsnog iets kwijtraakt.
 *
 * **Het gedrag is M7 en het vervoer waarop het wachtte bestaat niet meer** (besluit 2 van M7.0,
 * en besluit A van 7 september 2026). De vervoershelft van R-122 hing aan het inbeddingskanaal
 * van B8, en dat besluit is vervallen: de positie komt uit het aanmaakverzoek waarmee het
 * processysteem de instantie aanmaakt, en wordt als context meegeleverd net als de weergavemodus
 * en de contextkop (ontwerp 3.4). Aan deze component verandert dat niets: hij krijgt zijn positie
 * en geeft een verzoek terug.
 */

export interface SeriesPosition {
  /** Het hoeveelste dossier dit is, vanaf 1 geteld zoals een mens telt. */
  readonly nummer: number
  readonly totaal: number
  /** De instantie ernaast, of niets aan het begin of het eind van de reeks. */
  readonly vorige?: string
  readonly volgende?: string
}

/**
 * Waar deze instantie in de reeks staat, of niets (R-122).
 *
 * Hier en niet bij de aanroeper, om dezelfde reden als `laagOf` in de engine: de afbeelding van
 * een reeks naar een positie hoort bij het begrip, en twee kopieën lopen uiteen.
 *
 * Niets bij drie standen die alle drie "geen navigator" betekenen: geen reeks, een reeks van één,
 * of een reeks waar deze instantie niet in staat. Die laatste is geen fout om op te vangen maar
 * een aanroeper die iets anders zegt dan hij bedoelt, en dan is een navigator die ergens heen
 * springt erger dan geen navigator.
 */
export function positionIn(
  series: readonly string[] | undefined,
  instantieId: string,
): SeriesPosition | undefined {
  if (series === undefined || series.length < 2) return undefined
  const place = series.indexOf(instantieId)
  if (place === -1) return undefined
  const previous = series[place - 1]
  const next = series[place + 1]
  return {
    nummer: place + 1,
    totaal: series.length,
    ...(previous === undefined ? {} : { vorige: previous }),
    ...(next === undefined ? {} : { volgende: next }),
  }
}

export interface SeriesNavigatorProps {
  readonly positie: SeriesPosition
  readonly taal: Language
  /** Springen. De aanroeper schrijft eerst weg; zolang deze belofte loopt staat alles uit. */
  readonly onNavigate: (instantieId: string) => void
  /** Of er nu wordt weggeschreven. Dan is er niets te bedienen. */
  readonly bezig?: boolean
}

export function SeriesNavigator({
  positie: position,
  taal: language,
  onNavigate,
  bezig: busy = false,
}: SeriesNavigatorProps): ReactNode {
  return (
    <nav className="ef-reeks" aria-label={SERIES[language]}>
      <button
        type="button"
        className="ef-reeks__knop"
        disabled={busy || position.vorige === undefined}
        onClick={() => {
          if (position.vorige !== undefined) onNavigate(position.vorige)
        }}
      >
        {PREVIOUS[language]}
      </button>
      {/*
        Een statusregio en niet alleen tekst: na een sprong verandert dit getal en verder blijft
        het scherm erop lijken, dus zonder `role="status"` merkt wie niet kijkt er niets van.
      */}
      <span className="ef-reeks__stand" role="status">
        {language === 'nl'
          ? `Dossier ${position.nummer} van ${position.totaal}`
          : `Case ${position.nummer} of ${position.totaal}`}
      </span>
      <button
        type="button"
        className="ef-reeks__knop"
        disabled={busy || position.volgende === undefined}
        onClick={() => {
          if (position.volgende !== undefined) onNavigate(position.volgende)
        }}
      >
        {NEXT[language]}
      </button>
      {busy ? (
        <span className="ef-reeks__bezig" role="status">
          <Spinner />{' '}
          {BUSY[language]}
        </span>
      ) : null}
    </nav>
  )
}

const SERIES: Text = { nl: 'Navigatie binnen de reeks', en: 'Navigation within the series' }
const PREVIOUS: Text = { nl: 'Vorig dossier', en: 'Previous case' }
const NEXT: Text = { nl: 'Volgend dossier', en: 'Next case' }
const BUSY: Text = { nl: 'Bezig met opslaan', en: 'Saving' }
